<template>
  <div class="page-content">
    <div class="answer-record-page">
      <div class="page-header">
        <h2 class="page-title">答题记录</h2>
        <p class="page-subtitle">{{ pageSubtitle }}</p>
      </div>

      <div class="toolbar-card">
        <input
          v-model="keyword"
          type="text"
          class="search-input"
          placeholder="搜索问题 / 结果 / 账号 / 窗口 / OSS..."
          @keyup.enter="loadList(1)"
        />
        <button class="btn secondary" @click="loadList(1)">搜索</button>
        <el-select v-model="pageSize" class="page-size-select" @change="loadList(1)">
          <el-option :value="20" label="20 条/页" />
          <el-option :value="50" label="50 条/页" />
          <el-option :value="100" label="100 条/页" />
        </el-select>
      </div>

      <div class="table-card" v-loading="loading">
        <div class="table-header">
          <div class="header-cell time-col">答题时间</div>
          <div class="header-cell account-col">账号</div>
          <div class="header-cell window-col">窗口</div>
          <div class="header-cell question-col">问题</div>
          <div class="header-cell result-col">答题结果</div>
          <div class="header-cell duration-col">用时</div>
          <div class="header-cell image-col">截图</div>
        </div>

        <div v-if="rows.length === 0 && !loading" class="empty-state">
          <div class="empty-text">暂无答题记录</div>
        </div>

        <div v-for="row in rows" :key="row.id" class="table-row">
          <div class="cell time-col">{{ formatDateTime(row.answer_time) }}</div>
          <div class="cell account-col" :title="row.username">{{ row.username || '-' }}</div>
          <div class="cell window-col">{{ row.window_id || '-' }}</div>
          <div class="cell question-col" :title="row.question">{{ row.question || '-' }}</div>
          <div class="cell result-col" :title="row.answer_result">{{ row.answer_result || '-' }}</div>
          <div class="cell duration-col">{{ formatDuration(row.duration_ms) }}</div>
          <div class="cell image-col">
            <el-image
              v-if="row.image_url"
              :src="row.image_url"
              :preview-src-list="[row.image_url]"
              :initial-index="0"
              fit="cover"
              class="answer-record-thumb"
              :preview-teleported="true"
              :title="row.oss_file_name"
            />
            <span v-else-if="row.oss_file_name" class="oss-path-fallback" :title="row.oss_file_name">
              {{ row.oss_file_name }}
            </span>
            <span v-else>-</span>
          </div>
        </div>
      </div>

      <div v-if="total > 0" class="pagination-bar">
        <span>共 {{ total }} 条，第 {{ page }} / {{ totalPages }} 页</span>
        <div class="pagination-controls">
          <button class="btn secondary" :disabled="page <= 1" @click="loadList(page - 1)">上一页</button>
          <button class="btn secondary" :disabled="page >= totalPages" @click="loadList(page + 1)">下一页</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, ref } from 'vue'
import { ElMessage } from 'element-plus'
import answerRecordAPI from '../api/answerRecord'
import { canViewAllAnswerRecords, getStoredUser } from '../constants/answerRecord'

export default {
  name: 'AnswerRecordManagement',
  setup() {
    const loading = ref(false)
    const rows = ref([])
    const total = ref(0)
    const page = ref(1)
    const pageSize = ref(20)
    const keyword = ref('')

    const totalPages = computed(() => Math.max(1, Math.ceil(total.value / pageSize.value)))
    const pageSubtitle = computed(() =>
      canViewAllAnswerRecords(getStoredUser())
        ? '展示全部用户的测谎答题记录，按答题时间倒序。'
        : '仅展示本账号的测谎答题记录，按答题时间倒序。'
    )

    const formatDateTime = (value) => {
      if (!value) return '-'
      const d = new Date(value)
      if (Number.isNaN(d.getTime())) return '-'
      return d.toLocaleString(undefined, { hour12: false })
    }

    const formatDuration = (ms) => {
      if (ms == null || ms <= 0) return '-'
      if (ms < 1000) return `${ms}ms`
      const sec = ms / 1000
      return sec >= 10 ? `${Math.round(sec)}s` : `${sec.toFixed(1)}s`
    }

    const loadList = async (targetPage = page.value) => {
      loading.value = true
      try {
        const res = await answerRecordAPI.list(targetPage, pageSize.value, keyword.value)
        if (res.code === 200) {
          rows.value = res.data?.list || []
          total.value = res.data?.total || 0
          page.value = res.data?.page || targetPage
        } else {
          ElMessage.error(res.message || '加载失败')
        }
      } catch (e) {
        ElMessage.error(e.response?.data?.message || e.message || '加载失败')
      } finally {
        loading.value = false
      }
    }

    onMounted(() => {
      loadList(1)
    })

    return {
      loading,
      rows,
      total,
      page,
      pageSize,
      keyword,
      totalPages,
      pageSubtitle,
      formatDateTime,
      formatDuration,
      loadList
    }
  }
}
</script>

<style scoped>
.answer-record-page {
  max-width: 1400px;
  margin: 0 auto;
  padding: 24px;
}

.page-header {
  margin-bottom: 20px;
}

.page-title {
  font-size: 24px;
  font-weight: 600;
  color: #1d1d1f;
  margin-bottom: 8px;
}

.page-subtitle {
  color: #86868b;
  font-size: 14px;
}

.toolbar-card,
.table-card {
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e5e5ea;
  margin-bottom: 16px;
}

.toolbar-card {
  display: flex;
  gap: 12px;
  padding: 16px;
  flex-wrap: wrap;
  align-items: center;
}

.search-input {
  flex: 1;
  min-width: 220px;
  box-sizing: border-box;
  padding: 10px 14px;
  border: 1px solid #d2d2d7;
  border-radius: 10px;
  font-size: 14px;
}

.page-size-select {
  width: 120px;
}

.btn {
  border: none;
  border-radius: 10px;
  padding: 10px 16px;
  font-size: 14px;
  cursor: pointer;
}

.btn.secondary {
  background: #f5f5f7;
  color: #1d1d1f;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.table-header,
.table-row {
  display: grid;
  grid-template-columns: 150px 100px 70px 1.4fr 0.8fr 80px 160px;
  gap: 12px;
  align-items: center;
  padding: 12px 16px;
}

.table-header {
  border-bottom: 1px solid #e5e5ea;
  font-size: 13px;
  font-weight: 600;
  color: #6e6e73;
}

.table-row {
  border-bottom: 1px solid #f2f2f7;
  font-size: 13px;
}

.table-row:last-child {
  border-bottom: none;
}

.cell {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.image-col {
  overflow: visible;
  white-space: normal;
}

.answer-record-thumb {
  width: 160px;
  height: 90px;
  aspect-ratio: 16 / 9;
  border-radius: 6px;
  border: 1px solid #e5e5ea;
  cursor: zoom-in;
  display: block;
}

.answer-record-thumb :deep(.el-image__inner) {
  width: 160px;
  height: 90px;
  object-fit: cover;
}

.oss-path-fallback {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
  color: #86868b;
}

.empty-state {
  padding: 48px 16px;
  text-align: center;
}

.empty-text {
  color: #86868b;
}

.pagination-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #6e6e73;
  font-size: 13px;
}

.pagination-controls {
  display: flex;
  gap: 8px;
}

@media (max-width: 1100px) {
  .table-header {
    display: none;
  }

  .table-row {
    grid-template-columns: 1fr;
    gap: 6px;
    padding: 16px;
  }

  .cell::before {
    content: attr(data-label);
    display: block;
    font-size: 12px;
    color: #86868b;
    margin-bottom: 2px;
  }
}
</style>
